import { useState, useEffect } from "react";
import DashboardLayout from "../../components/layout/DashboardLayout";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Truck, Calendar, Loader2, X } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { formatCurrency, formatDate, getStatusColor, getStatusLabel } from "../../lib/utils";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function CustomerBookings() {
  const { token } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [vehicles, setVehicles] = useState({});
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");

  useEffect(() => {
    fetchBookings();
  }, [statusFilter]);

  const fetchBookings = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const params = statusFilter ? `?status=${statusFilter}` : "";
      
      const bookingsRes = await axios.get(`${API}/bookings${params}`, { headers, withCredentials: true });
      setBookings(bookingsRes.data);

      // Fetch vehicle details for each booking
      const vehicleIds = [...new Set(bookingsRes.data.map(b => b.vehicle_id))];
      const vehicleDetails = {};
      
      await Promise.all(vehicleIds.map(async (id) => {
        try {
          const res = await axios.get(`${API}/vehicles/${id}`);
          vehicleDetails[id] = res.data;
        } catch (e) {
          vehicleDetails[id] = null;
        }
      }));
      
      setVehicles(vehicleDetails);
    } catch (error) {
      console.error("Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm("Sei sicuro di voler cancellare questa prenotazione?")) return;
    
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API}/bookings/${bookingId}`, { headers, withCredentials: true });
      toast.success("Prenotazione cancellata");
      fetchBookings();
    } catch (error) {
      toast.error("Errore durante la cancellazione");
    }
  };

  return (
    <DashboardLayout title="Le Mie Prenotazioni">
      <div className="space-y-6" data-testid="customer-bookings">
        {/* Filters */}
        <div className="flex items-center gap-4">
          <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
            <SelectTrigger className="w-48" data-testid="status-filter">
              <SelectValue placeholder="Tutti gli stati" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tutti gli stati</SelectItem>
              <SelectItem value="pending">In attesa</SelectItem>
              <SelectItem value="confirmed">Confermate</SelectItem>
              <SelectItem value="completed">Completate</SelectItem>
              <SelectItem value="cancelled">Cancellate</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : bookings.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Calendar className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-bold text-lg mb-2">Nessuna prenotazione</h3>
              <p className="text-muted-foreground text-sm">
                {statusFilter ? "Nessuna prenotazione con questo stato" : "Non hai ancora effettuato prenotazioni"}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => {
              const vehicle = vehicles[booking.vehicle_id];
              return (
                <Card key={booking.booking_id} data-testid={`booking-${booking.booking_id}`}>
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex items-start gap-4">
                        <div className="bg-muted w-24 h-16 flex items-center justify-center">
                          {vehicle?.images?.[0] ? (
                            <img
                              src={vehicle.images[0].startsWith('http') ? vehicle.images[0] : `${process.env.REACT_APP_BACKEND_URL}${vehicle.images[0]}`}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Truck className="h-8 w-8 text-muted-foreground" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={getStatusColor(booking.status)}>
                              {getStatusLabel(booking.status)}
                            </Badge>
                            <span className="text-xs text-muted-foreground font-mono">
                              #{booking.booking_id.slice(-8)}
                            </span>
                          </div>
                          <h3 className="font-bold">
                            {vehicle ? `${vehicle.brand} ${vehicle.model}` : "Veicolo"}
                          </h3>
                          <p className="text-sm text-muted-foreground flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {formatDate(booking.start_date)} - {formatDate(booking.end_date)}
                          </p>
                          {booking.notes && (
                            <p className="text-sm text-muted-foreground mt-1">Note: {booking.notes}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-4">
                        <div className="text-right">
                          <p className="text-xs text-muted-foreground uppercase">Totale</p>
                          <p className="text-xl font-bold font-mono text-primary">
                            {formatCurrency(booking.total_price)}
                          </p>
                        </div>
                        {booking.status === "pending" && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={() => cancelBooking(booking.booking_id)}
                            data-testid={`cancel-booking-${booking.booking_id}`}
                          >
                            <X className="h-5 w-5" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
